import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useMetaApiInstance } from '../api';
import { HOST_QUERY_KEY } from '../constants';

import type { AxiosError } from 'axios';
import type { UseMutationOptions } from '@tanstack/react-query';
import type { FavoriteService } from '../constants/types';

type DeleteFavoriteServicesOptions = UseMutationOptions<
  FavoriteService[],
  AxiosError,
  FavoriteService,
  FavoriteService[]
>;

/**
 * 즐겨찾는 서비스를 제거하는 훅입니다.
 * @description
 * - react-query로 mutate를 제공합니다.
 */
const useDeleteFavoriteService = (options?: DeleteFavoriteServicesOptions) => {
  const client = useQueryClient();
  const { deleteFavoriteService } = useMetaApiInstance();

  const mutation = async (favorite: FavoriteService) => {
    const param = { serviceKeywords: [favorite.keyword] };
    const response = await deleteFavoriteService(param);

    return response;
  };

  return useMutation({
    mutationKey: [HOST_QUERY_KEY.DELETE_FAVORITE_SERVICE],
    mutationFn: mutation,
    onMutate: async favoriteToDelete => {
      await client.cancelQueries({ queryKey: [HOST_QUERY_KEY.GET_FAVORITE_SERVICE] });

      const previousFavorites =
        client.getQueryData<FavoriteService[]>([HOST_QUERY_KEY.GET_FAVORITE_SERVICE]) || [];

      client.setQueryData<FavoriteService[]>([HOST_QUERY_KEY.GET_FAVORITE_SERVICE], oldFavorites =>
        (oldFavorites || []).filter(favorite => favorite.keyword !== favoriteToDelete.keyword),
      );

      return previousFavorites;
    },
    onSuccess: res => {
      const newFavorites = res || [];
      client.setQueryData<FavoriteService[]>([HOST_QUERY_KEY.GET_FAVORITE_SERVICE], newFavorites);
    },
    onError: (error, favoriteToDelete, previousFavorites) => {
      if (previousFavorites) {
        client.setQueryData([HOST_QUERY_KEY.GET_FAVORITE_SERVICE], previousFavorites);
      }
    },
    ...options,
  });
};

export default useDeleteFavoriteService;
